import { useState, type CSSProperties, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { formatUnixDateTime, inputValueToUnix } from '@/shared/lib/datetime';
import { getListRows } from '@/shared/lib/pagination';
import { DateTimeInput } from '@/shared/ui/DateTimeInput';
import { Pagination } from '@/shared/ui/Pagination';
import { exportCommissions, fetchCommissionsList, type CommissionsQuery } from './api';

interface ServiceRef {
  id: number | string;
  title?: string;
}

interface CommissionRow extends Record<string, unknown> {
  object_id: number | string;
  create_time?: number;
  amount?: number | string;
  bid?: {
    cost?: number | string;
    work_type?: Array<{ services?: ServiceRef[] }>;
  } | null;
}

interface Filters {
  bidId: string;
  dateFrom: string;
  dateTo: string;
}

const emptyFilters: Filters = { bidId: '', dateFrom: '', dateTo: '' };

const labelStyle: CSSProperties = { display: 'block', fontWeight: 600, marginBottom: 4 };

const hasSearchData = (f: Filters) => Boolean(f.bidId.trim() || f.dateFrom || f.dateTo);

const toQuery = (f: Filters): CommissionsQuery => ({
  bid_id: f.bidId,
  create_time_from: inputValueToUnix(f.dateFrom),
  create_time_to: inputValueToUnix(f.dateTo),
});

const nowInputValue = () => dayjs().format('YYYY-MM-DDTHH:mm');

const getPagination = (data: unknown) =>
  (data as { pagination?: { pagesCount?: number; totalCount?: number } } | undefined)?.pagination;

export const CommissionsPage = () => {
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState<Filters>({ ...emptyFilters });
  const [applied, setApplied] = useState<Filters | null>(null);

  const { data, isFetching, error } = useQuery({
    queryKey: ['commissions', applied, page],
    queryFn: () => fetchCommissionsList(toQuery(applied!), page),
    enabled: applied !== null,
  });

  const exportMutation = useMutation({
    mutationFn: () => exportCommissions(toQuery(applied!)),
    onSuccess: (blob) => {
      const link = document.createElement('a');
      const stamp = (value: string) => (value ? dayjs(value).format('DD.MM.YYYY_HH.mm') : 'all');
      link.href = URL.createObjectURL(blob);
      link.download = `commission_report_${stamp(applied!.dateFrom)}_${stamp(applied!.dateTo)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    },
  });

  const rows = getListRows<CommissionRow>(data, ['commissions']);
  const pagination = getPagination(data);
  const totalPages = Number(pagination?.pagesCount) || 1;
  const canSubmit = hasSearchData(draft);
  const canExport = applied !== null && hasSearchData(applied) && Number(pagination?.totalCount) > 0;

  const setDraftField = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setPage(1);
    setApplied({ ...draft });
  };

  const onReset = () => {
    setDraft({ ...emptyFilters });
    setApplied(null);
    setPage(1);
  };

  return (
    <>
      <section className="content-header">
        <h1>Отчёт по комиссиям</h1>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-header with-border">
            <form onSubmit={onSearch}>
              <div className="form-group">
                <span style={labelStyle}>Номер заявки</span>
                <input
                  className="form-control input-sm"
                  style={{ width: 250 }}
                  placeholder="Введите номер"
                  value={draft.bidId}
                  onChange={(e) => setDraftField('bidId', e.target.value.replace(/\D/g, ''))}
                />
              </div>
              <div className="form-group">
                <span style={labelStyle}>От даты*</span>
                <DateTimeInput
                  value={draft.dateFrom}
                  max={draft.dateTo || nowInputValue()}
                  onChange={(v) => setDraftField('dateFrom', v)}
                />
              </div>
              <div className="form-group">
                <span style={labelStyle}>До даты*</span>
                <DateTimeInput
                  value={draft.dateTo}
                  min={draft.dateFrom || undefined}
                  max={nowInputValue()}
                  onChange={(v) => setDraftField('dateTo', v)}
                />
              </div>
              <button type="button" className="btn btn-warning" onClick={onReset} disabled={isFetching}>
                Очистить все поля
              </button>{' '}
              <button type="submit" className="btn btn-primary" disabled={!canSubmit || isFetching}>
                Найти
              </button>
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!canExport || isFetching || exportMutation.isPending}
                  onClick={() => exportMutation.mutate()}
                >
                  Выгрузить XLSX
                </button>
              </div>
            </form>
          </div>

          <div className="box-body">
            {isFetching && <p>Загрузка…</p>}
            {(error || exportMutation.isError) && (
              <div className="alert alert-danger">Ошибка загрузки отчёта по комиссиям</div>
            )}
            {applied !== null && !isFetching && !error && rows.length === 0 && (
              <div>За выбранный период операций комиссии нет</div>
            )}
            {rows.length > 0 && (
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>ID заявки</th>
                    <th>Дата/время операции комиссии</th>
                    <th>Вид работ</th>
                    <th>Сумма ремонта</th>
                    <th>Комиссия</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((commission, index) => (
                    <tr key={`${commission.object_id}-${index}`}>
                      <td>
                        <Link to={`/bids/bid/${Number(commission.object_id)}`}>{Number(commission.object_id)}</Link>
                      </td>
                      <td>{formatUnixDateTime(commission.create_time)}</td>
                      <td>
                        {commission.bid?.work_type?.map((type) =>
                          type.services?.map((service) => <div key={service.id}>{service.title}</div>),
                        )}
                      </td>
                      <td>{commission.bid?.cost}</td>
                      <td>{commission.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!isFetching && <Pagination page={page} totalPages={totalPages} onChange={setPage} />}
          </div>
        </div>
      </section>
    </>
  );
};
